/**
 * Parse distance/offset strings used in exercise config (vergence, depth, size, etc.).
 * Accepts values like "0.5 prism", "300 arcsec", "128 px", "70 cm" with optional i18n unit names.
 */

export enum DistanceUnit {
  Pixel = "px",
  Centimeter = "cm",
  PrismDiopter = "pdpt",
  Arcsecond = "arcsecond",
}

export interface ParsedDistance {
  value: number;
  unit: DistanceUnit | "";
}

const PIXEL_UNITS = [
  "px",
  "pixel",
  "píxel",
  "פיקסל",
  "بكسل",
  "بيكسل",
];

const CENTIMETER_UNITS = [
  "cm",
  "centimeter",
  "centímetro",
  "centimetro",
  "centimètre",
  "centimetre",
  "סנטימטר",
  'ס"מ',
  "سنتيمتر",
  "سم",
];

const MILLIMETER_UNITS = [
  "mm",
  "millimeter",
  "millimètre",
  "milímetro",
  "milimetro",
  "מילימטר",
  'מ"מ',
  "مليمتر",
  "مم",
];

const PRISM_UNITS = [
  "Δ",
  "prism diopter",
  "prism",
  "prisme",
  "prism-diopter",
  "dioptría de prisma",
  "dioptria de prisma",
  "dioptría prismática",
  "dioptria prismática",
  "dioptria prismatica",
  "dioptria de prisma",
  "דיופטרת פריזמטית",
  "דיופטריה",
  "פריזמה",
  "ديوبتر منشوري",
  "منشور",
  "ديوبتر",
  "درجة منشورية",
  "dioptria",
  "dioptría",
  "prisma",
  "pdpt",
  "prdpt",
  "PD",
  "pd",
];

const ARCSECOND_UNITS = [
  "arcsecond",
  "seconde d'arc",
  "arc sec",
  "segundo de arco",
  "arcosegundo",
  "arc second",
  "arcsec",
  "arcseg",
  "asec",
  "as",
  "″",
  '"',
  "''",
  "second",
  "seconde",
  "segundo",
  "segunda",
  "שניית קשת",
  "seg de arco",
  "ثانية قوسية",
  "ثانية",
  "ث",
  "שנ׳",
  "ثا",
];

const ARCMINUTE_UNITS = [
  "arcminute",
  "arcmin",
  "amin",
  "am",
  "′",
  "'",
  "minute d'arc",
  "minuto de arco",
  "דקת קשת",
  "دقيقة قوسية",
  "دقيقة",
  "د",
  "ד׳",
  "دق",
];

const DEGREE_UNITS = [
  "degree",
  "degrees",
  "deg",
  "degs",
  "°",
  "degré",
  "degré d'arc",
  "מעלה",
  "grado",
  "grados",
  "grau",
  "graus",
  "grau de arco",
  "grado de arco",
  "درجة",
  "°ق",
  "درجات",
  "درجه",
  "מ׳",
  "°م",
];

function normalizeUnit(raw: string): string {
  return raw.replace(/s$/i, "").trim();
}

/**
 * Parse a distance/offset string (e.g. "0.5 prism", "300 arcsec", "128 px") into value and unit.
 * If a number is passed, returns it with the default unit (or px).
 * @throws if the string cannot be parsed
 */
export function parseOffset(
  input: string | number,
  defaultUnit: DistanceUnit | null = null
): ParsedDistance {
  if (typeof input === "number") {
    return {
      value: input,
      unit: "",
    };
  }

  const offsetString = input.trim();
  const rx = /(-?\d*\.?\d*)\s*(.*)/;
  const match = rx.exec(offsetString);
  if (!match || match[1] === undefined || match[1] === "") {
    throw new Error(`Unable to parse distance: ${offsetString}`);
  }

  let value = Number(match[1]);
  const rawUnit = normalizeUnit(match[2] || "");

  if (!rawUnit) {
    return { value, unit: "" };
  }

  if (PIXEL_UNITS.includes(rawUnit)) {
    return { value, unit: DistanceUnit.Pixel };
  }
  if (CENTIMETER_UNITS.includes(rawUnit)) {
    return { value, unit: DistanceUnit.Centimeter };
  }
  if (MILLIMETER_UNITS.includes(rawUnit)) {
    return { value: value / 10, unit: DistanceUnit.Centimeter };
  }
  if (PRISM_UNITS.includes(rawUnit)) {
    return { value, unit: DistanceUnit.PrismDiopter };
  }
  if (ARCSECOND_UNITS.includes(rawUnit)) {
    return { value, unit: DistanceUnit.Arcsecond };
  }
  if (ARCMINUTE_UNITS.includes(rawUnit)) {
    return { value: value * 60, unit: DistanceUnit.Arcsecond };
  }
  if (DEGREE_UNITS.includes(rawUnit)) {
    return { value: value * 3600, unit: DistanceUnit.Arcsecond };
  }

  throw new Error(`Unable to parse distance: ${offsetString}`);
}

/** Default unit to apply when none is specified (for display/storage in exercise configs). */
export type OffsetDefaultUnit = "prism" | "arcsec";

const DEFAULT_UNIT_DISPLAY: Record<OffsetDefaultUnit, string> = {
  prism: "prism",
  arcsec: "arcsec",
};

/**
 * If the input has no unit, return the value with the default unit appended (e.g. "0.5" → "0.5 prism").
 * Use on blur or submit so the user sees the updated value with the default unit.
 * Returns the string to set; leaves input unchanged if it already has a unit or is invalid.
 */
export function applyDefaultOffsetUnit(
  input: string | number | null | undefined,
  defaultUnit: OffsetDefaultUnit
): string {
  const s = input == null ? "" : String(input).trim();
  if (s === "") return s;
  try {
    const parsed = parseOffset(input);
    if (parsed.unit === "") {
      return `${parsed.value} ${DEFAULT_UNIT_DISPLAY[defaultUnit]}`;
    }
  } catch {
    // leave invalid input as-is
  }
  return s;
}
